import {
  Button,
  Dialog,
  DialogContent,
  Popover,
  Box,
  Typography,
  CircularProgress,
  Snackbar,
  Alert,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { useAtom } from 'jotai';
import { useEffect, useState } from 'react';
import DownloadIcon from '@mui/icons-material/Download';
import { fetchbomdetails } from '../../atoms/Dashboard/DashboardAtom';
import { DownloadExcelFile, getColumnsList, prepareColumnsAndGroups } from '../Helper';
import { getCheckedColumns, setCheckedColumns, getColumnStorageKey } from '../columnSelection';
import CloseIcon from '@mui/icons-material/Close';
import SelectedTrackersModal from './selectedTrackersModal';
import settingsAtom from '../../atoms/settings/SettingsAtom';
import { getCurrentTerms } from '../projectTypeUtils';
import QuantityhDialog from './ModalComponent';
import { isReadOnly } from '../../utils/permissionUtils';

const TableComponent = (props) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const { open, setIsOpen, selectPart, isSvg, title, setOpenSelectedTrackersModal, openSelectedTrackersModal, selectedRow, trackerList, showViewSelectedButton } = props;
  const [loader, setLoader] = useState(false);
  const [, setBomList] = useAtom(fetchbomdetails);
  const LOCAL_STORAGE_KEY = getColumnStorageKey('bom');
  const [checkedList, setCheckedList] = useState(() => getCheckedColumns(LOCAL_STORAGE_KEY));
  const { data = [], initialColumns } = props;
  const [colValue, setColValue] = useState([]);
  const rows = data?.map((row, index) => ({ id: index, ...row }));
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, severity: 'success', message: '' });
  const [settingsData] = useAtom(settingsAtom)
  const { projectList } = settingsData;
  const currentProject = projectList.find(o => o.id === parseInt(localStorage.getItem("project_id")))
  const TERMS = getCurrentTerms();
  const [visible, setVisible] = useState(false);
  const [rowData] = useState({})


  const fetchData = async () => {
    setLoader(true);
    try {
      if (selectPart?.length || isSvg) {
        if (isSvg) {
          await setBomList(selectPart, isSvg);
        } else {
          await setBomList(selectPart);
        }
      } else {
        await setBomList('');
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoader(false);
    }
  };

  useEffect(() => {
    fetchData();
    if (initialColumns?.length) {
      setColValue(initialColumns);
      const stored = getCheckedColumns(LOCAL_STORAGE_KEY);
      if (!stored.length) {
        const defaultChecked = initialColumns.filter((item) => !item.hidden).map((item) => item.key);
        setCheckedList(defaultChecked);
        setCheckedColumns(LOCAL_STORAGE_KEY, defaultChecked);
      }
    }
    // eslint-disable-next-line
  }, []);

  const handleCancel = () => {
    setIsOpen(false);
  };

  const handlePopoverClick = (event) => {
    setAnchorEl(anchorEl ? null : event.currentTarget);
  };

  useEffect(() => {
    setCheckedColumns(LOCAL_STORAGE_KEY, checkedList);
    const updated = initialColumns.map((col) => ({
      ...col,
      hidden: !checkedList.includes(col.key),
    }));
    setColValue(updated);
    // eslint-disable-next-line
  }, [checkedList, initialColumns]);

  const onChange = (key) => {
    setCheckedList((prev) => {
      const updated = prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key];
      setCheckedColumns(LOCAL_STORAGE_KEY, updated);
      return updated;
    });
  };

  const openPopover = Boolean(anchorEl);
  const popoverId = openPopover ? 'columns-popover' : undefined;

  // const handleOpenModal = (data) => {
  //   setRowData(data)
  //   setVisible(true)
  // }

  const { visibleColumns, columnGroupingModel } = prepareColumnsAndGroups(colValue, '', '', '','', selectPart, [], ["part_name", "part_description"]);


  const handleDownload = async () => {
    setLoading(true);
    try {
      DownloadExcelFile(colValue, rows, currentProject, setSnackbar, "BOM Status for Blocks")
      setSnackbar({ open: true, message: 'Downloaded BOM Status for Blocks Data', severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, severity: 'error', message: 'Error downloading Excel.' });
      console.error(err);
    } finally {
      setLoading(false);
    }
  }

  return (
    <Box>
      <Dialog open={open} onClose={handleCancel} fullWidth maxWidth="xl">
        <DialogContent>
          <Box display="flex" justifyContent="space-between" gap={2} mb={2}>
            <Box>
              <Typography variant="span" fontSize={20} fontWeight={600}>{title}</Typography>
            </Box>
            <Box display="flex" gap={2}>
              {showViewSelectedButton && <Button variant="outlined" onClick={() => setOpenSelectedTrackersModal(true)}>{TERMS.VIEW_SELECTED}</Button>}
              <Button variant="outlined" startIcon={<DownloadIcon />} onClick={handleDownload} loading={loading} disabled ={isReadOnly()}>
                Download
              </Button>
              <Button variant="outlined" onClick={handlePopoverClick}>
                Columns
              </Button>
              <CloseIcon onClick={handleCancel} sx={{ cursor: "pointer" }} />
              <Popover
                id={popoverId}
                open={openPopover}
                anchorEl={anchorEl}
                onClose={() => setAnchorEl(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                PaperProps={{ style: { maxHeight: 380, width: 220 } }}
              >
                <Box p={1}>
                  <Typography variant="span" fontSize={16} fontWeight={600}>Columns List</Typography>
                  {getColumnsList(initialColumns, checkedList, onChange)}
                </Box>
              </Popover>
            </Box>
          </Box>

          {loader ? (
            <Box display="flex" height="80vh" alignItems="center" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : (
            <div style={{ height: "78vh", width: '100%', backgroundColor: '#ffffff' }}>
              <DataGrid
                rows={rows?.filter((item) => item.default_project_quantity !== 0)}
                columns={visibleColumns}
                experimentalFeatures={{ columnGrouping: true }}
                columnGroupingModel={columnGroupingModel}
                hideFooterPagination
                hideFooter={true}
                disableColumnMenu
                disableSelectionOnClick
                disableRowSelectionOnClick
                pagination={false}
                initialState={{
                  sorting: {
                    sortModel: [{ field: 'part_name', sort: 'asc' }],
                  },
                }}

                sx={{
                  '& .MuiDataGrid-sortIcon': {
                      color: '#ffffff',
                      cursor: "pointer"
                    },
                  '& .MuiDataGrid-columnSeparator': {
                    color: '#fff',
                  },
                  '& .MuiDataGrid-columnHeader': {
                    fontWeight: 600,
                    backgroundColor: "#f16253",
                    color: '#ffffff',
                  },
                  '& .MuiDataGrid-columnHeader--block_name': {
                    backgroundColor: "#f16253",
                    color: '#ffffff',
                  },
                  '& .MuiDataGrid-columnGroupHeader': {
                    backgroundColor: "#f16253",
                    color: '#ffffff',
                    fontWeight: 600,
                  },
                  '& .installed-header': {
                    backgroundColor: "#f16253",
                  },
                  '& .distributed-header': {
                    backgroundColor: "#f16253",
                  },
                  '& .MuiDataGrid-columnHeaderTitle': {
                    fontWeight: 600,
                    width: '100%',
                  },
                  '& .header-group': {
                    display: 'flex',
                    justifyContent: 'center',
                    textAlign: 'center',
                    width: '100%',
                  },
                }}
              />
            </div>

          )}
        </DialogContent>
        <SelectedTrackersModal
          open={openSelectedTrackersModal}
          onClose={() => setOpenSelectedTrackersModal(false)}
          selectedRows={selectedRow}
          trackerTypes={trackerList}
        />
      </Dialog>
      <QuantityhDialog 
        open={visible}
        onClose={() => setVisible(false)}
        rowData={rowData}
      
      />
        <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
          <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default TableComponent;
